import React from 'react';
import { Moon, Sun, Star } from 'lucide-react';
import { LifeStats } from '../../hooks/useLifeStats';
import Card from '../Card';

interface CosmicViewProps {
  stats: LifeStats;
}

// Astronomical constants
const EARTH_ORBIT_DAYS = 365.25; // Days for Earth to orbit the Sun
const MOON_ORBIT_DAYS = 29.53; // Days for the Moon to orbit Earth
const SOLAR_SYSTEM_AGE = 4.6; // Billion years
const UNIVERSE_AGE = 13.8; // Billion years
const MILKY_WAY_ROTATION_YEARS = 225000000; // Years for Milky Way to complete one rotation
const LIGHT_SPEED_KM_S = 299792.458; // Speed of light in km/s

const CosmicView: React.FC<CosmicViewProps> = ({ stats }) => {
  if (stats.lifeExpectancy <= 0) return null;
  
  // Calculate cosmic metrics
  const daysLived = stats.yearsLived * EARTH_ORBIT_DAYS;
  const earthOrbits = stats.yearsLived; // Same as years lived
  const moonOrbits = daysLived / MOON_ORBIT_DAYS; 
  const earthRotations = daysLived; // Same as days lived
  
  // Distance traveled through space
  // Earth orbits Sun at ~30 km/s
  const earthOrbitSpeed = 30; // km/s
  const distanceTraveledWithSun = daysLived * 24 * 60 * 60 * earthOrbitSpeed; // km
  const distanceInLightMinutes = distanceTraveledWithSun / (LIGHT_SPEED_KM_S * 60);
  const distanceInAU = distanceTraveledWithSun / 149597870.7; // 1 AU = 149,597,870.7 km
  
  // Human life in perspective
  const lifespanVsSolarSystem = (stats.lifeExpectancy / (SOLAR_SYSTEM_AGE * 1000000000)) * 100;
  const lifespanVsUniverse = (stats.lifeExpectancy / (UNIVERSE_AGE * 1000000000)) * 100;
  const percentOfMilkyWayRotation = (stats.lifeExpectancy / MILKY_WAY_ROTATION_YEARS) * 100;
  
  return (
    <div className="w-full max-w-4xl mx-auto">
      <Card className="p-6" animated={true}>
        <div className="flex items-center gap-3 mb-6">
          <Star size={24} className="text-purple-600 dark:text-catppuccin-mauve" />
          <h2 className="text-xl font-bold dark:text-catppuccin-text">Cosmic Perspective</h2>
        </div>

        <p className="text-gray-600 dark:text-catppuccin-subtext0 mb-8">
          During your {stats.yearsLived.toFixed(1)} years on Earth, you've been on an incredible journey through space.
        </p>
        
        {/* Orbits and rotations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          <div className="bg-blue-50 dark:bg-catppuccin-surface1 p-5 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <Sun size={24} className="text-yellow-500 dark:text-catppuccin-yellow" />
              <h3 className="font-semibold text-blue-800 dark:text-catppuccin-blue">Solar Journeys</h3>
            </div>

            <div className="flex items-center gap-4 mb-3">
              <div className="w-16 h-16 bg-yellow-100 dark:bg-catppuccin-surface2 rounded-full flex items-center justify-center">
                <span className="text-xl font-bold text-yellow-700 dark:text-catppuccin-yellow">{Math.floor(earthOrbits)}</span>
              </div>
              <div>
                <p className="font-medium dark:text-catppuccin-text">Orbits around the Sun</p>
                <p className="text-sm text-gray-600 dark:text-catppuccin-subtext0">Earth's journeys completed</p>
              </div>
            </div>

            <div>
              <p className="mb-1 text-sm font-medium text-blue-700 dark:text-catppuccin-blue">Distance traveled with the Sun</p>
              <p className="text-lg font-bold dark:text-catppuccin-text">{(distanceTraveledWithSun / 1000000000).toFixed(1)} billion km</p>
              <p className="text-sm text-gray-600 dark:text-catppuccin-subtext0">{distanceInAU.toFixed(1)} Astronomical Units</p>
              <p className="text-sm text-gray-600 dark:text-catppuccin-subtext0">{distanceInLightMinutes.toFixed(1)} light-minutes</p>
            </div>
          </div>
          
          <div className="bg-purple-50 dark:bg-catppuccin-surface1 p-5 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <Moon size={24} className="text-purple-700 dark:text-catppuccin-mauve" />
              <h3 className="font-semibold text-purple-800 dark:text-catppuccin-mauve">Lunar Cycles</h3>
            </div>

            <div className="flex items-center gap-4 mb-3">
              <div className="w-16 h-16 bg-purple-100 dark:bg-catppuccin-surface2 rounded-full flex items-center justify-center">
                <span className="text-xl font-bold text-purple-700 dark:text-catppuccin-mauve">{Math.floor(moonOrbits)}</span>
              </div>
              <div>
                <p className="font-medium dark:text-catppuccin-text">Lunar orbits observed</p>
                <p className="text-sm text-gray-600 dark:text-catppuccin-subtext0">Full cycles of the Moon</p>
              </div>
            </div>

            <div>
              <p className="mb-1 text-sm font-medium text-purple-700 dark:text-catppuccin-mauve">Earth rotations experienced</p>
              <p className="text-lg font-bold dark:text-catppuccin-text">{Math.floor(earthRotations).toLocaleString()} days</p>
              <p className="text-sm text-gray-600 dark:text-catppuccin-subtext0">Complete spins of our planet</p>
            </div>
          </div>
        </div>
        
        {/* Cosmic perspective bars */}
        <div className="mb-10">
          <h3 className="font-semibold mb-4 text-center dark:text-catppuccin-text">Your Life in Cosmic Context</h3>

          <div className="space-y-6">
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-gray-600 dark:text-catppuccin-subtext0">Your lifespan</span>
                <span className="text-sm text-gray-600 dark:text-catppuccin-subtext0">Age of Solar System (4.6 billion years)</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-catppuccin-surface2 rounded-full h-3">
                <div
                  className="bg-blue-500 dark:bg-catppuccin-blue h-3 rounded-full"
                  style={{ width: `${Math.max(0.001, lifespanVsSolarSystem)}%` }}
                ></div>
              </div>
              <p className="text-xs text-right mt-1 text-gray-500 dark:text-catppuccin-overlay0">
                {lifespanVsSolarSystem.toExponential(2)}% of Solar System's age
              </p>
            </div>

            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-gray-600 dark:text-catppuccin-subtext0">Your lifespan</span>
                <span className="text-sm text-gray-600 dark:text-catppuccin-subtext0">Age of Universe (13.8 billion years)</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-catppuccin-surface2 rounded-full h-3">
                <div
                  className="bg-purple-500 dark:bg-catppuccin-mauve h-3 rounded-full"
                  style={{ width: `${Math.max(0.001, lifespanVsUniverse)}%` }}
                ></div>
              </div>
              <p className="text-xs text-right mt-1 text-gray-500 dark:text-catppuccin-overlay0">
                {lifespanVsUniverse.toExponential(2)}% of Universe's age
              </p>
            </div>

            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-gray-600 dark:text-catppuccin-subtext0">Your lifespan</span>
                <span className="text-sm text-gray-600 dark:text-catppuccin-subtext0">One Milky Way rotation (225 million years)</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-catppuccin-surface2 rounded-full h-3">
                <div
                  className="bg-green-500 dark:bg-catppuccin-green h-3 rounded-full"
                  style={{ width: `${Math.max(0.001, percentOfMilkyWayRotation)}%` }}
                ></div>
              </div>
              <p className="text-xs text-right mt-1 text-gray-500 dark:text-catppuccin-overlay0">
                {percentOfMilkyWayRotation.toExponential(4)}% of one galactic year
              </p>
            </div>
          </div>
        </div>
        
        {/* Perspective note */}
        <div className="text-center text-gray-600 dark:text-catppuccin-subtext0 italic mt-8 border-t border-gray-100 dark:border-catppuccin-surface2 pt-4">
          <p>
            "The cosmos is within us. We are made of star-stuff.
            We are a way for the universe to know itself." - Carl Sagan
          </p>
        </div>
      </Card>
    </div>
  );
};

export default CosmicView; 